/**
 * Color utility functions for dark mode color mapping.
 *
 * Provides HSL conversion and luminance inversion utilities
 * used to generate dark mode color variants that preserve hue
 * while inverting brightness.
 */

export interface HSL {
  readonly h: number; // 0-360
  readonly s: number; // 0-100
  readonly l: number; // 0-100
}

/**
 * Converts a hex color string to HSL components.
 * Accepts 6-digit hex with or without leading '#'.
 */
export function hexToHsl(hex: string): HSL {
  const normalized = hex.startsWith('#') ? hex.slice(1) : hex;
  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

    if (max === r) {
      h = ((g - b) / delta + (g < b ? 6 : 0)) * 60;
    } else if (max === g) {
      h = ((b - r) / delta + 2) * 60;
    } else {
      h = ((r - g) / delta + 4) * 60;
    }
  }

  return {
    h: Math.round(h * 100) / 100,
    s: Math.round(s * 10000) / 100,
    l: Math.round(l * 10000) / 100,
  };
}

/**
 * Converts HSL components back to a 6-digit hex color string (with '#' prefix).
 */
export function hslToHex(hsl: HSL): string {
  const { h, s, l } = hsl;
  const sNorm = s / 100;
  const lNorm = l / 100;

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNorm - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else {
    r = c; g = 0; b = x;
  }

  const toHex = (val: number): string => {
    const clamped = Math.max(0, Math.min(255, Math.round((val + m) * 255)));
    return clamped.toString(16).padStart(2, '0');
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Inverts the luminance of a hex color while preserving hue and saturation.
 * Maps luminance L to (100 - L).
 */
export function invertLuminance(hex: string): string {
  const hsl = hexToHsl(hex);
  return hslToHex({ h: hsl.h, s: hsl.s, l: 100 - hsl.l });
}

/**
 * Generates the dark mode variant of a color.
 * Inverts luminance while preserving hue and saturation.
 */
export function generateDarkModeColor(hex: string): string {
  return invertLuminance(hex);
}
